import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { format } from 'date-fns';
import ja from 'date-fns/locale/ja';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// FilterListIcon は使用しないため削除
import ClearIcon from '@mui/icons-material/Clear';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip } from '@mui/material';

// 日付のフォーマット処理
const formatDate = (dateString) => {
  try {
    if (!dateString) return '日付なし';
    const date = new Date(dateString);
    // 無効な日付のチェック
    if (isNaN(date.getTime())) return '日付形式エラー';
    return format(date, 'yyyy年MM月dd日 HH:mm', { locale: ja });
  } catch (e) {
    console.error('日付フォーマットエラー:', e);
    return '日付形式エラー';
  }
};

// 会話カードコンポーネント
const ConversationCard = ({ conversation, onClick, searchQuery, favorites = [], onToggleFavorite }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (text, event) => {
    event.stopPropagation(); // カード全体のクリックイベントを防ぐ
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // 2秒後に元に戻す
    });
  };


  
  // お気に入り状態の確認
  const isFavorite = favorites.includes(conversation.id);



  // 検索キーワードのハイライト処理
  const highlightText = (text, query) => {
    if (!query || !text || query.trim() === '') return text;
    
    try {
      // クエリを複数のキーワードに分割
      const keywords = query.trim().split(/\s+/);
      
      // 各キーワードに対してハイライト処理
      let highlightedText = text;
      keywords.forEach(keyword => {
        if (keyword) {
          const regex = new RegExp(`(${keyword})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
        }
      });
      
      return highlightedText;
    } catch (e) {
      console.error('ハイライト処理エラー:', e);
      return text;
    }
  };
  
  // 検索キーワードがある場合はハイライトしたテキストを生成
  const contentWithHighlight = searchQuery && conversation.content 
    ? highlightText(conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : ''), searchQuery)
    : conversation.content 
      ? conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : '') 
      : '内容なし';

  const filename = conversation.sourceFilename || conversation.filename || conversation.file;

  return (
    <Card className="conversation-card" sx={{ mb: 2 }} elevation={2}>
      <CardActionArea onClick={() => onClick(conversation)}>
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom>
            {conversation.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formatDate(conversation.timestamp)}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 1.5 }} 
            dangerouslySetInnerHTML={{ __html: contentWithHighlight }}
          />

          
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              label={conversation.sender || 'unknown'} 
              size="small" 
              color={conversation.sender === 'user' ? 'primary' : 'secondary'}
              sx={{ mb: 0.5 }}
            />
            {conversation.inContext !== undefined && (
              <Chip 
                label={conversation.inContext ? 'コンテキスト内' : 'コンテキスト外'} 
                size="small" 
                variant="outlined"
                sx={{ mb: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation(); // カード全体のクリックイベントを防止
                  onClick(conversation);
                }}
              />
            )}
            {conversation.imageData && (
              <Chip 
                label="画像あり" 
                size="small" 
                color="info"
                sx={{ mb: 0.5 }}
              />
            )}
            {/* ファイル名の表示 */}
            {filename && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Tooltip title={filename} placement="top">
                  <Chip 
                    label={filename} 
                    size="small" 
                    variant="outlined"
                    sx={{ 
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  />
                </Tooltip>
                <Tooltip title={copied ? "コピーしました！" : "ファイル名をコピー"}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleCopy(filename, e)}
                    sx={{ p: 0.2 }}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {/* お気に入りボタン */}
            <Chip 
              icon={isFavorite ? <StarIcon /> : <StarOutlineIcon />} 
              label={isFavorite ? 'お気に入り済み' : 'お気に入り'}
              size="small" 
              color={isFavorite ? "warning" : "default"}
              variant="outlined"
              sx={{ mb: 0.5 }}
              onClick={(e) => {
                e.stopPropagation(); // カード全体のクリックイベントを防止
                onToggleFavorite && onToggleFavorite(conversation.id);
              }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// 日付グループコンポーネント
const DateGroup = ({ date, conversations, onConversationClick, searchQuery, favorites, onToggleFavorite }) => {
  // 日付のフォーマット処理
  const formatGroupDate = (dateString) => {
    try {
      if (!dateString) return '日付なし';
      const date = new Date(dateString);
      // 無効な日付のチェック
      if (isNaN(date.getTime())) return '日付形式エラー';
      return format(date, 'yyyy年MM月dd日', { locale: ja });
    } catch (e) {
      console.error('日付グループフォーマットエラー:', e);
      return '日付形式エラー';
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {formatGroupDate(date)}
      </Typography>
      
      {conversations.map((conversation, index) => (
        <ConversationCard 
          key={conversation.id || `conv-${index}`} 
          conversation={conversation} 
          onClick={onConversationClick}
          searchQuery={searchQuery}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </Box>
  );
};

// フィルターパネルコンポーネント
const FilterPanel = ({ filters, onFilterChange, searchQuery, onSearch }) => {
  const [dateRange, setDateRange] = useState(filters.dateRange || '');
  const [tagFilter, setTagFilter] = useState(filters.tags[0] || '');
  const [keyword, setKeyword] = useState(searchQuery || '');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(filters.showOnlyFavorites || false);
  
  // カスタム期間選択用の状態
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customDateActive, setCustomDateActive] = useState(false);
  
  // カスタム期間の表示用テキスト
  const getCustomDateRangeText = () => {
    if (!customDateActive || !startDate) return '';
    
    const start = format(startDate, 'yyyy/MM/dd', { locale: ja });
    const end = endDate ? format(endDate, 'yyyy/MM/dd', { locale: ja }) : '現在';
    
    return `${start} 〜 ${end}`;
  };
  
  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    
    // カスタム期間選択が選ばれた場合
    if (value === 'custom') {
      setCustomDateDialogOpen(true);
      return;
    }
    
    // 通常の期間選択の場合
    setDateRange(value);
    setCustomDateActive(false);
    onFilterChange({ 
      dateRange: value,
      customDateRange: null
    });
  };
  
  const handleTagFilterChange = (event) => {
    const value = event.target.value;
    setTagFilter(value);
    onFilterChange({ tags: value ? [value] : [] });
  };
  
  const handleKeywordChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    onSearch(value);
  };
  
  // カスタム期間選択を適用
  const applyCustomDateRange = () => {
    // 開始日が選択されていない場合は処理しない
    if (!startDate) return;
    
    setCustomDateActive(true);
    setDateRange('custom');
    setCustomDateDialogOpen(false);
    
    onFilterChange({
      dateRange: 'custom',
      customDateRange: {
        startDate: startDate.toISOString(),
        endDate: endDate ? endDate.toISOString() : null
      }
    });
  };
  
  // お気に入りフィルターの切り替え
  const handleFavoriteFilterChange = (event) => {
    const checked = event.target.checked;
    setShowOnlyFavorites(checked);
    onFilterChange({
      ...filters,
      showOnlyFavorites: checked
    });
  };

  // フィルターをクリア
  const clearFilters = () => {
    setDateRange('');
    setTagFilter('');
    setKeyword('');
    setShowOnlyFavorites(false);
    setCustomDateActive(false);
    setStartDate(null);
    setEndDate(null);
    // お気に入りフィルターも含めて全てリセット
    onFilterChange({ 
      dateRange: null, 
      tags: [],
      customDateRange: null,
      showOnlyFavorites: false
    });
    onSearch('');
  };
  
  return (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12} sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            sx={{ float: 'right' }}
          >
            全てクリア
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="date-range-label">期間</InputLabel>
            <Select
              labelId="date-range-label"
              value={dateRange}
              label="期間"
              onChange={handleDateRangeChange}
            >
              <MenuItem value=""><em>指定なし</em></MenuItem>
              <MenuItem value="week">直近一週間</MenuItem>
              <MenuItem value="month">直近一ヶ月</MenuItem>
              <MenuItem value="year">直近一年</MenuItem>
              <MenuItem value="custom">カスタム期間...</MenuItem>
            </Select>
          </FormControl>
          {customDateActive && getCustomDateRangeText() && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {getCustomDateRangeText()}
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="tag-filter-label">送信者</InputLabel>
            <Select
              labelId="tag-filter-label"
              value={tagFilter}
              label="送信者"
              onChange={handleTagFilterChange}
            >
              <MenuItem value="">全て</MenuItem>
              <MenuItem value="ユーザー">ユーザー</MenuItem>
              <MenuItem value="AI">AI</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyFavorites}
                  onChange={handleFavoriteFilterChange}
                  color="warning"
                />
              }
              label="お気に入りのみ表示"
            />
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="キーワード検索"
            variant="outlined"
            value={keyword}
            onChange={handleKeywordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {keyword && (
                    <IconButton edge="end" size="small" onClick={() => {
                      setKeyword('');
                      onSearch('');
                    }}>
                      <ClearIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* 全てクリアボタンは上部に移動しました */}
      </Grid>
      
      {/* カスタム期間選択ダイアログ */}
      <Dialog open={customDateDialogOpen} onClose={() => setCustomDateDialogOpen(false)}>
        <DialogTitle>期間を選択</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            カレンダーから期間を選択してください。
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '300px' }}>
            <Typography variant="subtitle2">開始日</Typography>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              placeholderText="開始日を選択"
              isClearable
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              }
            />
            
            <Typography variant="subtitle2">終了日 (空白の場合は現在まで)</Typography>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              placeholderText="終了日を選択"
              isClearable
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={startDate}
              customInput={
                <TextField
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarMonthIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateDialogOpen(false)}>キャンセル</Button>
          <Button 
            onClick={applyCustomDateRange} 
            variant="contained" 
            color="primary"
          >
            適用
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TimelineView = ({ 
  conversations, 
  onConversationClick, 
  searchQuery, 
  filters,
  onFilterChange,
  onSearch,
  favorites,
  onToggleFavorite
 }) => {

  // 会話を日付でグループ化
  const groupByDate = (conversations) => {
    const groups = {};
    const defaultGroup = '0000-00-00'; // 無効な日付用のグループ
    
    conversations.forEach(conversation => {
      try {
        // timestampが存在しない場合はデフォルトグループに入れる
        if (!conversation.timestamp) {
          if (!groups[defaultGroup]) {
            groups[defaultGroup] = {
              date: null,
              conversations: []
            };
          }
          groups[defaultGroup].conversations.push(conversation);
          return;
        }
        
        const date = new Date(conversation.timestamp);
        
        // 無効な日付のチェック
        if (isNaN(date.getTime())) {
          console.warn('無効な日付:', conversation.timestamp);
          if (!groups[defaultGroup]) {
            groups[defaultGroup] = {
              date: null,
              conversations: []
            };
          }
          groups[defaultGroup].conversations.push(conversation);
          return;
        }
        
        const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        
        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: conversation.timestamp,
            conversations: []
          };
        }
        
        groups[dateKey].conversations.push(conversation);
      } catch (e) {
        console.error('日付グループ化エラー:', e);
        // エラーが発生した場合はデフォルトグループに入れる
        if (!groups[defaultGroup]) {
          groups[defaultGroup] = {
            date: null,
            conversations: []
          };
        }
        groups[defaultGroup].conversations.push(conversation);
      }
    });
    
    // 日付順にソート（新しい順）
    return Object.values(groups).sort((a, b) => {
      // nullの日付は最後に表示
      if (a.date === null) return 1;
      if (b.date === null) return -1;
      
      try {
        return new Date(b.date) - new Date(a.date);
      } catch (e) {
        console.error('ソートエラー:', e);
        return 0;
      }
    });
  };
  
  // 検索とフィルターを適用
  const filteredConversations = conversations.filter(conversation => {
    // 検索クエリによるフィルタリング
    if (searchQuery && searchQuery.trim() !== '') {
      // クエリを複数のキーワードに分割
      const keywords = searchQuery.trim().toLowerCase().split(/\s+/);
      
      // 各キーワードについて検索
      for (const keyword of keywords) {
        if (!keyword) continue; // 空のキーワードはスキップ
        
        // 検索対象フィールドの確認
        const contentMatch = conversation.content && conversation.content.toLowerCase().includes(keyword);
        const senderMatch = conversation.sender && conversation.sender.toLowerCase().includes(keyword);
        const idMatch = conversation.id && conversation.id.toLowerCase().includes(keyword);
        
        // いずれかのフィールドにキーワードが含まれていなければ除外（AND検索）
        if (!(contentMatch || senderMatch || idMatch)) {
          return false;
        }
      }
    }
    
    // お気に入りフィルター
    if (filters.showOnlyFavorites && !favorites.includes(conversation.id)) {
      return false;
    }
    
    // 送信者フィルター (tagsの代わりにsenderを使用)
    if (filters.tags.length > 0) {
      // tagsフィルターをsenderフィルターとして使用
      const senderFilter = filters.tags[0]; // 最初のタグを送信者フィルターとして使用
      if (senderFilter === 'ユーザー' && conversation.sender !== 'user') return false;
      if (senderFilter === 'AI' && conversation.sender !== 'ai') return false;
    }
    
    // 日付範囲フィルター
    if (filters.dateRange) {
      const now = new Date();
      const convDate = new Date(conversation.timestamp);
      
      // 無効な日付の場合はフィルタリングしない
      if (isNaN(convDate.getTime())) return true;
      
      // カスタム期間選択の場合
      if (filters.dateRange === 'custom' && filters.customDateRange) {
        const { startDate, endDate } = filters.customDateRange;
        
        // 開始日のチェック
        if (startDate) {
          const start = new Date(startDate);
          if (convDate < start) return false;
        }
        
        // 終了日のチェック（指定されていれば）
        if (endDate) {
          const end = new Date(endDate);
          // 終了日の23:59:59までを含める
          end.setHours(23, 59, 59, 999);
          if (convDate > end) return false;
        }
      } else {
        // 通常の期間選択の場合
        switch (filters.dateRange) {
          case 'week':
            if ((now - convDate) > 7 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'month':
            // 全くクリアボタンは上部に表示しない
            return false;
          case 'year':
            if ((now - convDate) > 365 * 24 * 60 * 60 * 1000) return false;
            break;
          default:
            break;
        }
      }
    }
    
    return true;
  });
  
  const dateGroups = groupByDate(filteredConversations);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>時系列ビュー</Typography>
      <Divider sx={{ mb: 3 }} />
      
      <FilterPanel 
        filters={filters} 
        onFilterChange={onFilterChange}
        searchQuery={searchQuery}
        onSearch={onSearch}
      />
      
      {dateGroups.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          表示する会話がありません
        </Typography>
      ) : (
        dateGroups.map((group, index) => (
          <DateGroup 
            key={index} 
            date={group.date} 
            conversations={group.conversations} 
            onConversationClick={onConversationClick}
            searchQuery={searchQuery}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
          />
        ))
      )}
      

    </Box>
  );
};

export default TimelineView;
